import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Lightbulb,
  Plus,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import { Badge, Button, cn } from '../../../ui';
import type {
  EstimateInputs,
  EstimateResult,
} from '../../../features/sprintboard/engine/estimation';
import {
  CHANGE_TYPE_OPTIONS,
  COMPLEXITY_OPTIONS,
  EXTRA_WORK_ADVANCED,
  EXTRA_WORK_COMMON,
  UNKNOWNS_OPTIONS,
  effectiveEstimate,
  formatDays,
  makeOverride,
  shouldRecommendSpike,
  toggleInList,
} from '../../../features/sprintboard/estimate/taskEstimateModel';
import type {
  EstimateOverride,
  EstimateSource,
} from '../../../features/sprintboard/estimate/types';
import type { KbSource } from '../../../api';

export interface EstimateWizardProps {
  /** Controlled engine inputs (the wizard answers). */
  inputs: EstimateInputs;
  /** Called with the next inputs whenever the engineer changes an answer. */
  onChange: (next: EstimateInputs) => void;
  /** The deterministic engine result for the current inputs (computed upstream). */
  result: EstimateResult;
  /** Persists the current estimate. */
  onSave: () => void;
  saving?: boolean;
  saveLabel?: string;
  /** When the inputs came from Vector, the model's rationale (shown on result). */
  aiRationale?: string[];
  /** Provenance badge on the result step. */
  source?: EstimateSource;
  /** Current manual override (if any) — shown + edited on the result step. */
  override?: EstimateOverride | null;
  /**
   * Called when the engineer sets/edits/clears the override. When omitted the
   * result step shows the override read-only (no editing controls).
   */
  onOverrideChange?: (override: EstimateOverride | undefined) => void;
  /** KB citations Vector grounded the suggestion in (shown on the result step). */
  kbSources?: KbSource[];
  /** Bump to reset the wizard back to step 1 (e.g. when re-opened). */
  resetToken?: number;
  /** Bump to jump straight to the result step (e.g. right after an AI pre-fill). */
  goToResultToken?: number;
}

const STEP_TITLES = [
  'Change type',
  'What makes it harder',
  'Extra work',
  'Unknowns',
];
const TOTAL_STEPS = STEP_TITLES.length; // 4 question steps, then the result

/** A selectable option card (radio or checkbox styling shared). */
function OptionCard({
  selected,
  onClick,
  label,
  description,
  kind,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  description: string;
  kind: 'radio' | 'checkbox';
}) {
  return (
    <button
      type="button"
      role={kind === 'radio' ? 'radio' : 'checkbox'}
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
        selected
          ? 'border-accent bg-accent-soft/30'
          : 'border-surface-border bg-surface-1 hover:border-zinc-600',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border',
          kind === 'radio' ? 'rounded-full' : 'rounded',
          selected ? 'border-accent bg-accent' : 'border-zinc-500',
        )}
        aria-hidden="true"
      >
        {selected && (
          <span
            className={cn(
              'bg-white',
              kind === 'radio' ? 'h-1.5 w-1.5 rounded-full' : 'h-2 w-2 rounded-sm',
            )}
          />
        )}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-zinc-100">{label}</span>
        <span className="mt-0.5 block text-xs text-zinc-500">{description}</span>
      </span>
    </button>
  );
}

/**
 * The compact 4-step estimate wizard + result. Each step is deliberately small:
 * pick a change type, tick what makes it harder, tick the extra work it drags
 * in, and say how well-understood it is — then the result screen shows a
 * transparent, itemized breakdown plus the effort / story points / confidence,
 * so the engineer sees exactly where the number came from before saving.
 */
export function EstimateWizard({
  inputs,
  onChange,
  result,
  onSave,
  saving,
  saveLabel = 'Save Estimate',
  aiRationale,
  source,
  override,
  onOverrideChange,
  kbSources,
  resetToken,
  goToResultToken,
}: EstimateWizardProps) {
  // step 0..3 are questions; step === TOTAL_STEPS is the result.
  const [step, setStep] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset to the first step whenever the parent bumps the reset token.
  useEffect(() => {
    setStep(0);
  }, [resetToken]);

  // Jump to the result step when the parent bumps the result token.
  useEffect(() => {
    if (goToResultToken !== undefined && goToResultToken > 0) {
      setStep(TOTAL_STEPS);
    }
  }, [goToResultToken]);

  // Reveal the advanced extra-work group if any advanced item is already set.
  useEffect(() => {
    if (inputs.extraWork.some((w) => EXTRA_WORK_ADVANCED.some((o) => o.value === w))) {
      setShowAdvanced(true);
    }
  }, [inputs.extraWork]);

  const recommendSpike = useMemo(() => shouldRecommendSpike(inputs), [inputs]);
  const isResult = step === TOTAL_STEPS;

  const set = (patch: Partial<EstimateInputs>) =>
    onChange({ ...inputs, ...patch });

  return (
    <div className="space-y-5">
      {/* Progress */}
      <div className="flex items-center gap-1.5">
        {STEP_TITLES.map((title, i) => (
          <div key={title} className="flex flex-1 flex-col gap-1">
            <div
              className={cn(
                'h-1 rounded-full transition-colors',
                i < step || isResult
                  ? 'bg-accent'
                  : i === step
                    ? 'bg-accent/60'
                    : 'bg-surface-3',
              )}
            />
          </div>
        ))}
      </div>

      {!isResult && (
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Step {step + 1} of {TOTAL_STEPS} · {STEP_TITLES[step]}
        </p>
      )}

      {/* Step 1 — Change type (radio) */}
      {step === 0 && (
        <div role="radiogroup" aria-label="Change type" className="space-y-2">
          {CHANGE_TYPE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              kind="radio"
              selected={inputs.changeType === opt.value}
              onClick={() => set({ changeType: opt.value })}
              label={opt.label}
              description={opt.description}
            />
          ))}
          {inputs.changeType === 'custom' && (
            <div className="space-y-3 rounded-xl border border-surface-border bg-surface-1 px-3.5 py-3">
              <div>
                <label
                  htmlFor="custom-change-label"
                  className="mb-1 block text-xs font-medium text-zinc-300"
                >
                  Custom change label
                </label>
                <input
                  id="custom-change-label"
                  type="text"
                  value={inputs.customChangeType ?? ''}
                  onChange={(e) => set({ customChangeType: e.target.value })}
                  placeholder="e.g. Data backfill"
                  className="w-full rounded-lg border border-surface-border bg-surface-2 px-2.5 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="custom-base-days"
                  className="mb-1 block text-xs font-medium text-zinc-300"
                >
                  Base effort (ideal days)
                </label>
                <input
                  id="custom-base-days"
                  type="number"
                  min={0}
                  step={0.5}
                  value={inputs.customBaseDays ?? ''}
                  onChange={(e) =>
                    set({
                      customBaseDays:
                        e.target.value === ''
                          ? undefined
                          : Number(e.target.value),
                    })
                  }
                  placeholder="e.g. 2"
                  className="w-full rounded-lg border border-surface-border bg-surface-2 px-2.5 py-1.5 text-sm tabular-nums text-zinc-100 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  Complexity and extra work are added on top of this base.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — Complexity (checkboxes) */}
      {step === 1 && (
        <div className="space-y-2">
          <p className="text-sm text-zinc-400">
            Tick anything that applies. Each adds effort and lowers confidence.
          </p>
          {COMPLEXITY_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              kind="checkbox"
              selected={inputs.complexity.includes(opt.value)}
              onClick={() =>
                set({ complexity: toggleInList(inputs.complexity, opt.value) })
              }
              label={opt.label}
              description={opt.description}
            />
          ))}
        </div>
      )}

      {/* Step 3 — Extra work (common + "+ More") */}
      {step === 2 && (
        <div className="space-y-2">
          <p className="text-sm text-zinc-400">
            What extra work does this drag in?
          </p>
          {EXTRA_WORK_COMMON.map((opt) => (
            <OptionCard
              key={opt.value}
              kind="checkbox"
              selected={inputs.extraWork.includes(opt.value)}
              onClick={() =>
                set({ extraWork: toggleInList(inputs.extraWork, opt.value) })
              }
              label={opt.label}
              description={opt.description}
            />
          ))}
          {showAdvanced ? (
            EXTRA_WORK_ADVANCED.map((opt) => (
              <OptionCard
                key={opt.value}
                kind="checkbox"
                selected={inputs.extraWork.includes(opt.value)}
                onClick={() =>
                  set({ extraWork: toggleInList(inputs.extraWork, opt.value) })
                }
                label={opt.label}
                description={opt.description}
              />
            ))
          ) : (
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-accent-fg hover:bg-surface-3"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              More options
            </button>
          )}
        </div>
      )}

      {/* Step 4 — Unknowns (radio) + spike recommendation */}
      {step === 3 && (
        <div className="space-y-2">
          <div role="radiogroup" aria-label="Unknowns" className="space-y-2">
            {UNKNOWNS_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.value}
                kind="radio"
                selected={inputs.unknowns === opt.value}
                onClick={() => set({ unknowns: opt.value })}
                label={opt.label}
                description={opt.description}
              />
            ))}
          </div>
          {recommendSpike && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-3">
              <Lightbulb
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-300"
                aria-hidden="true"
              />
              <div className="text-sm text-amber-200">
                <p className="font-medium">Consider a spike first.</p>
                <p className="mt-0.5 text-xs text-amber-200/80">
                  With major unknowns, a time-boxed investigation de-risks the
                  work before you commit to a delivery estimate.
                </p>
                <button
                  type="button"
                  onClick={() => set({ changeType: 'spike' })}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-400/40 px-2.5 py-1 text-xs font-medium text-amber-100 hover:bg-amber-500/20"
                >
                  Make it a Spike
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {isResult && (
        <EstimateResultView
          result={result}
          aiRationale={aiRationale}
          source={source}
          override={override}
          onOverrideChange={onOverrideChange}
          kbSources={kbSources}
        />
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-surface-border pt-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ChevronLeft className="h-4 w-4" />}
          disabled={step === 0 || saving}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Back
        </Button>
        {isResult ? (
          <Button variant="primary" loading={saving} onClick={onSave}>
            {saveLabel}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            rightIcon={<ChevronRight className="h-4 w-4" />}
            onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
          >
            {step === TOTAL_STEPS - 1 ? 'See estimate' : 'Next'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The §6 result: transparent breakdown + effort / story points / confidence. */
export function EstimateResultView({
  result,
  aiRationale,
  source,
  override,
  onOverrideChange,
  kbSources,
}: {
  result: EstimateResult;
  aiRationale?: string[];
  source?: EstimateSource;
  override?: EstimateOverride | null;
  onOverrideChange?: (override: EstimateOverride | undefined) => void;
  kbSources?: KbSource[];
}) {
  const editable = typeof onOverrideChange === 'function';
  const computed = {
    effortDays: result.engineeringEffortDays,
    storyPoints: result.storyPoints,
    confidence: result.confidence,
  };
  const effective = effectiveEstimate(computed, override);
  const overridden = effective.overridden;

  // Enabling the override seeds it from the computed numbers (remark still
  // required); disabling clears it entirely.
  const toggleOverride = () => {
    if (!onOverrideChange) return;
    if (override) {
      onOverrideChange(undefined);
    } else {
      onOverrideChange(
        makeOverride({
          effortDays: computed.effortDays,
          storyPoints: computed.storyPoints,
          confidence: computed.confidence,
          remark: '',
        }),
      );
    }
  };

  const patchOverride = (patch: Partial<EstimateOverride>) => {
    if (!onOverrideChange || !override) return;
    onOverrideChange({ ...override, ...patch });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-zinc-100">
          {result.isSpike ? 'Recommended spike timebox' : 'Engineering estimate'}
        </h3>
        {source === 'ai' && (
          <Badge tone="accent">
            <Sparkles className="h-3 w-3" aria-hidden="true" /> Vector
          </Badge>
        )}
        {overridden && (
          <Badge tone="warning">
            <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />{' '}
            Overridden
          </Badge>
        )}
      </div>

      {/* Breakdown — every added day itemized so the number is explainable. */}
      <div className="rounded-xl border border-surface-border bg-surface-1">
        <ul className="divide-y divide-surface-border">
          {result.breakdown.map((line, i) => (
            <li
              key={`${line.label}-${i}`}
              className="flex items-center justify-between px-3.5 py-2 text-sm"
            >
              <span className="text-zinc-300">{line.label}</span>
              <span className="tabular-nums text-zinc-400">
                +{formatDays(line.days)}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between bg-surface-2/60 px-3.5 py-2.5 text-sm font-semibold">
            <span className="text-zinc-100">
              {result.isSpike ? 'Timebox' : 'Engineering effort'}
            </span>
            <span className="tabular-nums text-zinc-100">
              {formatDays(result.engineeringEffortDays)}
            </span>
          </li>
        </ul>
      </div>

      {/* Headline metrics — EFFECTIVE numbers (override when present). */}
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Effort" value={formatDays(effective.effortDays)} />
        <Metric label="Story points" value={String(effective.storyPoints)} />
        <Metric label="Confidence" value={`${effective.confidence}%`} />
      </div>
      {overridden && (
        <p className="-mt-1 text-center text-xs text-zinc-500">
          Computed: {formatDays(computed.effortDays)} · {computed.storyPoints} pts ·{' '}
          {computed.confidence}% confidence
        </p>
      )}

      {/* Override — editable in the flow; read-only elsewhere. */}
      {editable ? (
        <div className="rounded-xl border border-surface-border bg-surface-1 px-3.5 py-3">
          <label className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-200">
              <SlidersHorizontal className="h-4 w-4 text-zinc-400" aria-hidden="true" />
              Override the computed estimate
            </span>
            <input
              type="checkbox"
              role="switch"
              checked={overridden}
              onChange={toggleOverride}
              className="h-4 w-4 accent-accent"
              aria-label="Override the computed estimate"
            />
          </label>
          {override && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <OverrideNumber
                  label="Effort (days)"
                  value={override.effortDays}
                  min={0}
                  step={0.5}
                  onChange={(n) => patchOverride({ effortDays: n })}
                />
                <OverrideNumber
                  label="Story points"
                  value={override.storyPoints}
                  min={0}
                  step={1}
                  onChange={(n) => patchOverride({ storyPoints: n })}
                />
                <OverrideNumber
                  label="Confidence %"
                  value={override.confidence}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(n) => patchOverride({ confidence: n })}
                />
              </div>
              <div>
                <label
                  htmlFor="override-remark"
                  className="mb-1 block text-xs font-medium text-zinc-300"
                >
                  Remark <span className="text-amber-400">(required)</span>
                </label>
                <textarea
                  id="override-remark"
                  value={override.remark}
                  onChange={(e) => patchOverride({ remark: e.target.value })}
                  rows={2}
                  placeholder="Why is the computed estimate being overridden?"
                  className={cn(
                    'w-full rounded-lg border bg-surface-2 px-2.5 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none',
                    override.remark.trim()
                      ? 'border-surface-border focus:border-accent'
                      : 'border-amber-500/50 focus:border-amber-400',
                  )}
                />
              </div>
              <button
                type="button"
                onClick={() => onOverrideChange?.(undefined)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-zinc-400 hover:bg-surface-3 hover:text-zinc-200"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                Clear override
              </button>
            </div>
          )}
        </div>
      ) : (
        overridden &&
        override?.remark.trim() && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-200">
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
              Override remark
            </p>
            <p className="mt-1 text-sm text-amber-100/90">{override.remark}</p>
          </div>
        )
      )}

      {aiRationale && aiRationale.length > 0 && (
        <div className="rounded-xl border border-accent/30 bg-accent-soft/20 px-3.5 py-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-accent-fg">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Why Vector suggested this
          </p>
          <ul className="space-y-1 text-xs text-zinc-300">
            {aiRationale.map((r, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-zinc-600">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {kbSources && kbSources.length > 0 && (
        <div className="rounded-xl border border-surface-border bg-surface-1 px-3.5 py-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-zinc-300">
            <BookOpen className="h-3.5 w-3.5 text-zinc-400" aria-hidden="true" />
            Grounded in team knowledge base
          </p>
          <ul className="space-y-1.5 text-xs text-zinc-400">
            {kbSources.map((s, i) => (
              <li key={`${s.uri ?? s.text}-${i}`} className="flex gap-1.5">
                <span className="text-zinc-600">•</span>
                {s.uri ? (
                  <a
                    href={s.uri}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-accent-fg hover:underline"
                  >
                    <span className="truncate">{kbSourceLabel(s)}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                  </a>
                ) : (
                  <span className="line-clamp-2">{kbSourceLabel(s)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** A compact override number input with a label. */
function OverrideNumber({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-zinc-400">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-lg border border-surface-border bg-surface-2 px-2.5 py-1.5 text-sm tabular-nums text-zinc-100 focus:border-accent focus:outline-none"
      />
    </label>
  );
}

/** Best-effort human label for a KB source: last URI segment, else the snippet. */
function kbSourceLabel(s: KbSource): string {
  if (s.uri) {
    const trimmed = s.uri.replace(/\/+$/, '');
    const seg = trimmed.split('/').pop();
    return seg && seg.trim() ? decodeURIComponent(seg) : s.uri;
  }
  const text = s.text.trim();
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-center">
      <p className="text-lg font-semibold tabular-nums text-zinc-100">{value}</p>
      <p className="mt-0.5 text-xs text-zinc-500">{label}</p>
    </div>
  );
}

export default EstimateWizard;
